import { Document } from "@tiptap/extension-document";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Mention } from "@tiptap/extension-mention";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Text } from "@tiptap/extension-text";
import { useEditor, useEditorState, type Editor } from "@tiptap/react";
import type { ExpressionOperator } from "../ExpressionEditor";
import type { FormulaData } from "../ExpressionEditor.types";
import { ID_FOR_NO_TEMPLATE } from "../ExpressionTemplateSelect/ExpressionTemplateSelect";
import { CharacterConstraint } from "../extensions/CharacterConstraint";
import { CustomValueInTemplate } from "../extensions/CustomValueInTemplate/CustomValueInTemplate";
import { PasteTransform } from "../extensions/PasteTransform/PasteTransform";
import { getSuggestionConfig } from "../utils/getSuggestionConfig";

interface UseExpressionEditorProps {
  variables: Array<string>;
  operators: Array<ExpressionOperator>;
  otherElements: Array<ExpressionOperator>;
  invalidVariablesOrFunctionsMessage: string;
  initialFormulaData?: FormulaData;
  onChange: (formulaData: FormulaData) => void;
}

export function useExpressionEditor({
  variables,
  operators,
  otherElements,
  invalidVariablesOrFunctionsMessage,
  initialFormulaData,
  onChange
}: UseExpressionEditorProps) {
  const editor: Editor = useEditor({
    extensions: [
      CharacterConstraint.configure({
        allowedOperatorsAndFunctions: [
          ...operators.map((o) => o.value),
          ...otherElements.map((o) => o.value)
        ],
        errorMessages: {
          invalidVariablesOrFunctions: invalidVariablesOrFunctionsMessage
        }
      }),
      CustomValueInTemplate,
      Document.extend({
        // this prevents additional paragraphs, so we don't need the overhead of serializing them
        content: "block"
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            // reroute the shortcut since we only create one single paragraph -> line breaks = <br/>
            Enter: () => editor.commands.setHardBreak()
          };
        }
      }),
      Mention.configure({
        renderHTML({ node }) {
          const varLabel = node.attrs.label ?? node.attrs.id;
          return [
            "span",
            {
              class: "expression-variable",
              "data-type": "mention",
              "data-id": varLabel
            },
            `${varLabel}`
          ];
        },
        renderText({ node }) {
          return `${node.attrs.label ?? node.attrs.id}`;
        },
        deleteTriggerWithBackspace: true,
        suggestion: getSuggestionConfig(variables)
      }),
      Paragraph,
      PasteTransform.configure({
        allowedVariables: variables
      }),
      Placeholder.configure({
        placeholder: "Geben Sie hier die gewünschte Formel ein ..."
      }),
      Text
    ],
    onCreate: ({ editor }) => {
      // TODO in https://node-energy.atlassian.net/browse/WUP-8864: remove typeof checks of `initialFormulaData` after BE integrates new API
      if (typeof initialFormulaData === "string") {
        editor.view.pasteText(initialFormulaData as unknown as string);
        return;
      }

      if (!initialFormulaData || initialFormulaData.template_id) return;
      // simple init via paste, only used for non-template formulas
      editor.view.pasteText(initialFormulaData.formula);
    },
    onUpdate({ editor }) {
      const storage = editor.storage.customValueInTemplate;

      // storage could be {}
      if (!storage) {
        return;
      }

      const templateId = storage.templateId;
      const customValues = storage.values;
      const hasCustomValues = Object.keys(customValues || {}).length > 0;

      const newFormulaData: FormulaData = {
        formula: editor.getText(),
        template_id: templateId !== ID_FOR_NO_TEMPLATE ? templateId : undefined,
        custom_values: hasCustomValues ? customValues : undefined
      };

      onChange(newFormulaData);
    }
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isEditable: editor.isEditable,
      selectedTemplateId:
        editor.storage.customValueInTemplate?.templateId ?? null
    })
  });

  function insertVariable(variable: string) {
    editor
      .chain()
      .focus()
      .insertContent([
        { type: "mention", attrs: { id: variable } },
        { type: "text", text: " " }
      ])
      .run();
  }

  function insertText(text: string) {
    editor
      .chain()
      .focus()
      .insertContent([{ type: "text", text }])
      .run();
  }

  function clearExpression() {
    editor.chain().focus().clearContent().run();
  }

  return {
    editor,
    editorState,
    insertVariable,
    insertText,
    clearExpression
  };
}
